// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { createReference } from '@medplum/core';
import type { Appointment, ChargeItem, Encounter, Provenance } from '@medplum/fhirtypes';
import {
  APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM,
  APPOINTMENT_MISSED_VISIT_FEE_AMOUNT_USD,
  APPOINTMENT_MISSED_VISIT_FEE_CODE,
  APPOINTMENT_MISSED_VISIT_FEE_DEFINITION_URL,
  APPOINTMENT_WAIVER_PROVENANCE_REASON_SYSTEM,
} from '@polarissleep/shared';
import { withTenantScope } from '../utils/tenant';

export type MissedVisitDisposition = 'no-show' | 'cancelled';
export type MissedVisitException = 'emergency' | 'hospitalization' | 'technical-failure' | undefined;

export function isLateCancellation(appointment: Appointment, now = new Date()): boolean {
  return !!appointment.start && new Date(appointment.start).getTime() - now.getTime() < 24 * 60 * 60 * 1000;
}

export async function recordMissedVisitFee(
  medplum: MedplumClient,
  appointment: Appointment,
  disposition: MissedVisitDisposition,
  options: { waived?: boolean; exception?: MissedVisitException; now?: Date } = {}
): Promise<ChargeItem | undefined> {
  if (
    options.exception ||
    options.waived ||
    (disposition === 'cancelled' && !isLateCancellation(appointment, options.now))
  ) {
    if (options.exception) {
      await recordWaiver(medplum, appointment, disposition, options.exception);
    }
    return undefined;
  }
  if (!appointment.id) {
    throw new Error('Appointment must have an ID before charging missed visit fee');
  }
  const existing = await medplum.searchResources('ChargeItem', {
    identifier: `${APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM}|${appointment.id}`,
    _count: '1',
  });
  if (existing[0]) {
    return existing[0] as ChargeItem;
  }
  const encounters = await medplum.searchResources('Encounter', {
    appointment: `Appointment/${appointment.id}`,
    _count: '1',
  });
  const encounter = encounters[0] as Encounter | undefined;
  const patient =
    encounter?.subject ?? appointment.participant?.find((p) => p.actor?.reference?.startsWith('Patient/'))?.actor;
  if (!patient?.reference) {
    throw new Error(`Patient missing for appointment ${appointment.id}`);
  }
  return medplum.createResource<ChargeItem>(
    withTenantScope({
      resourceType: 'ChargeItem',
      status: 'billable',
      subject: patient as ChargeItem['subject'],
      context: encounter?.id ? createReference(encounter) : undefined,
      service: [{ reference: `Appointment/${appointment.id}` }],
      occurrenceDateTime: appointment.start ?? new Date().toISOString(),
      code: {
        coding: [
          {
            system: 'http://polarissleep.com/charge-item-definitions',
            code: APPOINTMENT_MISSED_VISIT_FEE_CODE,
            display: 'No-show fee',
          },
        ],
        text: 'No-show fee',
      },
      quantity: { value: 1 },
      priceOverride: { value: APPOINTMENT_MISSED_VISIT_FEE_AMOUNT_USD, currency: 'USD' },
      definitionCanonical: [APPOINTMENT_MISSED_VISIT_FEE_DEFINITION_URL],
      identifier: [{ system: APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM, value: appointment.id }],
    } satisfies ChargeItem)
  );
}

async function recordWaiver(
  medplum: MedplumClient,
  appointment: Appointment,
  disposition: MissedVisitDisposition,
  exception: MissedVisitException
): Promise<void> {
  const profile = medplum.getProfile();
  if (!appointment.id || !profile) {
    throw new Error('Staff profile required to record fee waiver');
  }
  const provenance: Provenance = {
    resourceType: 'Provenance',
    target: [createReference(appointment)],
    recorded: new Date().toISOString(),
    reason: [
      {
        coding: [
          {
            system: APPOINTMENT_WAIVER_PROVENANCE_REASON_SYSTEM,
            code: exception ?? 'courtesy-waiver',
            display: exception ?? 'Courtesy waiver',
          },
        ],
      },
    ],
    agent: [{ who: createReference(profile) }],
    entity: [{ role: 'source', what: { reference: `Appointment/${appointment.id}` } }],
    activity: { coding: [{ system: APPOINTMENT_WAIVER_PROVENANCE_REASON_SYSTEM, code: disposition }] },
  };
  await medplum.createResource(withTenantScope(provenance));
}
